import React from "react";
import { ShieldCheck, FileText, Eye, Download } from "lucide-react";

/* ===================== STYLES ===================== */
import styles from "./Compliance.module.scss";

const policies = [
  {
    id: 1,
    title: "Data Retention & Deletion Policy",
    fileName: "data-deletion.pdf",
    description:
      "Outlines how long data is stored and the secure methods used for permanent destruction after the retention period.",
  },
  {
    id: 2,
    title: "DPDPA Privacy Notice",
    fileName: "dpdpa-notice.pdf",
    description:
      "Explains to Data Principals how their personal data is collected, processed, and protected under Indian law.",
  },
  {
    id: 3,
    title: "Terms of Service (ToS)",
    fileName: "tos.pdf",
    description:
      "The primary legal contract establishing the rules, user rights, and limitations for accessing the software.",
  },
  {
    id: 4,
    title: "Acceptable Use Policy (AUP)",
    fileName: "aup.pdf",
    description:
      "Dictates what constitutes prohibited behavior, illegal activities, and service abuse on the platform.",
  },
  {
    id: 5,
    title: "Information Security Management",
    fileName: "ismp.pdf",
    description:
      "The master document detailing the company's overall security posture, aligning with standard frameworks like ISO 27001.",
  },
  {
    id: 6,
    title: "Bring Your Own Device (BYOD)",
    fileName: "byod.pdf",
    description:
      "Establishes security requirements and access controls for employees utilizing personal hardware for work.",
  },
  {
    id: 7,
    title: "Secure SDLC Policy",
    fileName: "ssdlc.pdf",
    description:
      "Mandates security checks, peer code reviews, and vulnerability scanning throughout the software development life cycle.",
  },
  {
    id: 8,
    title: "Backup & Disaster Recovery",
    fileName: "backup-recovery.pdf",
    description:
      "Defines the frequency of database backups and the standard operating procedure for restoring services during an outage.",
  },
];

const Compliance = () => {
  /* =========================
     HANDLERS
  ========================= */
  const handleView = (fileName) => {
    // Opens the PDF in a new browser tab
    window.open(`/policies/${fileName}`, "_blank", "noopener,noreferrer");
  };

  const handleDownload = (fileName) => {
    // Creates a temporary link to trigger the download
    const link = document.createElement("a");
    link.href = `/policies/${fileName}`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["admin-comp__page"]}>
      {/* HEADER */}
      <div className={styles["admin-comp__header"]}>
        <div className={styles["admin-comp__icon-wrapper"]}>
          <ShieldCheck size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-comp__title-group"]}>
          <h2 className={styles["admin-comp__title"]}>
            IT & Compliance Policies
          </h2>
          <p className={styles["admin-comp__subtitle"]}>
            Review or download our latest policy documentation.
          </p>
        </div>
      </div>

      {/* DOCUMENT LIBRARY CARD */}
      <div className={styles["admin-comp__card"]}>
        <div className={styles["admin-comp__table-wrapper"]}>
          <table className={styles["admin-comp__table"]}>
            <thead>
              <tr>
                <th className={styles["admin-comp__th"]}>Policy Document</th>
                <th className={styles["admin-comp__th"]}>Description</th>
                <th
                  className={styles["admin-comp__th"]}
                  style={{ width: "120px" }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {policies.map((policy) => (
                <tr key={policy.id} className={styles["admin-comp__tr"]}>
                  {/* POLICY NAME & ICON */}
                  <td className={styles["admin-comp__td"]}>
                    <div className={styles["admin-comp__policy-name-cell"]}>
                      <div className={styles["admin-comp__file-icon"]}>
                        <FileText size={20} strokeWidth={2.5} />
                      </div>
                      <div>
                        <h4 className={styles["admin-comp__policy-title"]}>
                          {policy.title}
                        </h4>
                        <p className={styles["admin-comp__policy-filename"]}>
                          {policy.fileName}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* DESCRIPTION */}
                  <td className={styles["admin-comp__td"]}>
                    <p className={styles["admin-comp__policy-desc"]}>
                      {policy.description}
                    </p>
                  </td>

                  {/* ACTIONS */}
                  <td className={styles["admin-comp__td"]}>
                    <div className={styles["admin-comp__actions-wrapper"]}>
                      <button
                        className={styles["admin-comp__icon-btn"]}
                        onClick={() => handleView(policy.fileName)}
                        title="View Policy"
                        aria-label={`View ${policy.title}`}
                      >
                        <Eye size={18} strokeWidth={2.5} />
                      </button>
                      <button
                        className={`${styles["admin-comp__icon-btn"]} ${styles["admin-comp__icon-btn--download"]}`}
                        onClick={() => handleDownload(policy.fileName)}
                        title="Download Policy"
                        aria-label={`Download ${policy.title}`}
                      >
                        <Download size={18} strokeWidth={2.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Compliance;
